// WeekAppointmentTile: one booking on the PA Week grid, drawn exactly as Antonin's export draws it
// (`src/components/AppointmentTile/AppointmentTile.tsx`, the served kit's 'practitioner' tone) with the export's calendar overrides (`src/tokens/encounter-overrides.css` "Calendar tiles (Samer,
// 2026-10-21)"): a hairline card tinted by its LOCATION, a 4 px edge in the location's colour, the patient name over
// the location glyph and the time, the status as its glyph only (no chip, no word; Antonin, 22 Oct) with the status
// name as its instant tooltip, and the note as a
// short label (first three words) whose whole text opens in a tooltip. A past tile steps back to grey; a half-hour
// tile is one line; a block is the striped tile with its reason.
// Presentational only: the grid places it (top / height / overlap) and owns drag-and-drop. The look is
// `src/styles/showcase-calendar-week.css`.
// Stand-in for the Signal DS `PACalendarAppointmentTile` (PROD-3310): the DS tile has no location tint, no past
// state, no note tooltip and draws its status as a filled badge.
// Kept from the PA (the DS tile drew them; the export's tile does not): the patient importance code and the flag,
// drawn in the export's own meta language (12 px, meta ink) after the time.
import type { DraggableAttributes, useDraggable } from '@dnd-kit/core';
import { Tooltip, TooltipContent, TooltipTrigger, type PACalendarAppointment } from '@scripsteam/scrips-signal-ds';
import { APPT_GLYPH, LOCATION_GLYPH } from '@/scheduling/lib/appointment-glyphs';
import { StatusGlyph } from '@/shared/ui/components/status-glyph';
import type { GlyphKind } from '@/shared/ui/components/status-glyph-tokens';
import { shortNote } from './short-note';
import {
  tileLocation,
  tileStatus,
  tileStatusWord,
  WEEK_TILE_COMPACT_BELOW,
  WEEK_TILE_NOTE_ABOVE,
  type TileLocation,
  type TileStatus,
} from './week-layout';

/** The location glyph: the Day rows' own (`locationGlyph`), so one place reads the same on both views. */
const LOCATION_ICON = { office: LOCATION_GLYPH.office, home: LOCATION_GLYPH.home, virtual: LOCATION_GLYPH.virtual } as const satisfies Record<TileLocation, unknown>;
const ICON_CLASS = 'size-3 shrink-0';
const LOCATION_LABEL: Record<TileLocation, string> = { office: 'In clinic', home: 'Home visit', virtual: 'Video' };

/** The export's status → glyph shape (`StatusGlyph` `STATUS_KIND`). The PA's finer stages fold in through `tileStatus`:
 *  checked in draws as arrived ('half'), confirmed as booked ('empty'), the export's `stage` rule. */
const STATUS_KIND: Record<TileStatus, GlyphKind> = {
  'in-session': 'full',
  arrived: 'half',
  completed: 'done',
  'no-show': 'alert',
  booked: 'empty',
  cancelled: 'empty',
};

export interface WeekAppointmentTileProps {
  appointment: PACalendarAppointment;
  /** The drawn height in px, from the grid: it decides the one-line layout and the note line. */
  height: number;
  /** The booking's end has passed. */
  past?: boolean;
  onClick?: () => void;
  /** Set when the tile can be dragged: the tile's button is the drag activator (dnd-kit ref, a11y attributes, listeners). */
  dragHandle?: { ref: (el: HTMLElement | null) => void; attributes: DraggableAttributes; listeners: ReturnType<typeof useDraggable>['listeners'] };
}

export function WeekAppointmentTile({ appointment: apt, height, past = false, onClick, dragHandle }: WeekAppointmentTileProps) {
  const drag = dragHandle ? { ref: dragHandle.ref, ...dragHandle.attributes, ...dragHandle.listeners } : {};
  const note = apt.note?.trim() ?? '';
  if (apt.isBlock) {
    return (
      <button
        type="button"
        className="cal-week-tile"
        data-slot="pa-calendar-week-tile"
        data-block=""
        {...drag}
        onClick={onClick}
        aria-label={`Blocked${note ? `: ${note}` : ''}, ${apt.timeRange}`}
      >
        <span className="cal-week-tile-row">
          {apt.isRecurring ? <APPT_GLYPH.recurring className={ICON_CLASS} aria-hidden /> : <LOCATION_GLYPH.block className={ICON_CLASS} aria-hidden />}
          <span className="cal-week-tile-name">{note || 'Blocked'}</span>
        </span>
        <span className="cal-week-tile-time">{apt.timeRange}</span>
      </button>
    );
  }
  const location = tileLocation(apt.profileLocation ?? apt.locationName);
  const LocationGlyph = LOCATION_ICON[location];
  const status = tileStatus(apt.status);
  // The status name (the PA's finer stages keep their own word: "Checked in", "Confirmed") is the glyph's tooltip.
  const statusWord = tileStatusWord(apt.status);
  // A half-hour tile is too short for two lines: the name and the time share one.
  const compact = height < WEEK_TILE_COMPACT_BELOW;
  const showNote = note !== '' && !compact && height > WEEK_TILE_NOTE_ABOVE;
  const importance = apt.patientImportance?.trim();
  // The recurring, importance and flag marks: in the meta row after the time, or (a compact tile has no meta row) in
  // the one row between the name and the status glyph.
  const marks = (
    <>
      {apt.isRecurring && <APPT_GLYPH.recurring className={ICON_CLASS} aria-label="Recurring" />}
      {importance && (
        <span className="cal-week-tile-importance" title={apt.patientImportanceDisplay?.trim() || importance}>
          {importance}
        </span>
      )}
      {apt.hasFlag && <APPT_GLYPH.flag className={ICON_CLASS} aria-label="Flagged" />}
    </>
  );
  const hasMarks = Boolean(apt.isRecurring || importance || apt.hasFlag);
  return (
    <button
      type="button"
      className="cal-week-tile"
      data-slot="pa-calendar-week-tile"
      data-status={status}
      data-location={location}
      data-past={past ? '' : undefined}
      data-compact={compact ? '' : undefined}
      {...drag}
      onClick={onClick}
      aria-label={`${apt.patientName}, ${statusWord}, ${apt.timeRange}`}
    >
      <span aria-hidden className="cal-week-tile-edge" data-slot="pa-calendar-week-tile-edge" />
      <span className="cal-week-tile-body">
        <span className="cal-week-tile-row">
          {compact && (
            <span className="cal-week-tile-row-icon" title={LOCATION_LABEL[location]}>
              <LocationGlyph className={ICON_CLASS} aria-hidden />
            </span>
          )}
          <span className="cal-week-tile-name">
            {apt.patientName}
            {compact && <span className="cal-week-tile-inline-time"> {apt.timeRange}</span>}
          </span>
          {compact && hasMarks && (
            <span className="cal-week-tile-marks" data-slot="pa-calendar-week-tile-marks">
              {marks}
            </span>
          )}
          <Tooltip>
            <TooltipTrigger
              delay={0}
              closeDelay={0}
              render={
                <span className="cal-week-tile-status" data-slot="pa-calendar-week-tile-status">
                  <StatusGlyph kind={STATUS_KIND[status]} title={statusWord} />
                </span>
              }
            />
            <TooltipContent>{statusWord}</TooltipContent>
          </Tooltip>
        </span>
        {!compact && (
          <span className="cal-week-tile-meta">
            <span className="cal-week-tile-loc" title={apt.locationName?.trim() || LOCATION_LABEL[location]}>
              <LocationGlyph className={ICON_CLASS} aria-hidden />
            </span>
            <span className="cal-week-tile-time">{apt.timeRange}</span>
            {marks}
          </span>
        )}
        {showNote && (
          <Tooltip>
            <TooltipTrigger
              delay={200}
              closeDelay={0}
              render={
                <span className="cal-week-tile-note" data-slot="pa-calendar-week-tile-note">
                  {shortNote(note)}
                </span>
              }
            />
            <TooltipContent side="right" align="start">
              {note}
            </TooltipContent>
          </Tooltip>
        )}
      </span>
    </button>
  );
}
